/**
 * <upsell-variant-select> — compact inline size/colour <select> pair for a
 * Complete the Look card (snippets/upsell-product.liquid, inline_variant_select
 * mode only). Matches the chosen option values to a real variant ID using
 * the product's own variants JSON (already in the page, no fetch), writes
 * it into the form's hidden `id` input, and mirrors the matched variant's
 * availability onto the submit button - the actual add-to-cart submit is
 * untouched, this only keeps that hidden input correct before it fires.
 */
(function () {
  'use strict';

  if (customElements.get('upsell-variant-select')) return;

  class UpsellVariantSelect extends HTMLElement {
    constructor() {
      super();
      this.onChange = this.onChange.bind(this);
    }

    connectedCallback() {
      try {
        this.variants = JSON.parse(this.dataset.variants || '[]');
      } catch (e) {
        this.variants = [];
      }

      this.form = this.closest('form');
      this.hiddenInput = this.form ? this.form.querySelector('[data-upsell-variant-id]') : null;
      this.submitBtn = this.form ? this.form.querySelector('[data-quick-add-btn]') : null;

      this.addEventListener('change', this.onChange);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
    }

    onChange() {
      // Every option occupies a slot here, whether it rendered as a real
      // <select> (multi-value) or a hidden input carrying its one fixed
      // value (single-value, no dropdown shown) - see upsell-product.liquid.
      const sources = Array.from(this.querySelectorAll('select, input[type="hidden"][data-option-position]'));
      if (!sources.length || !this.variants.length) return;

      const selectedValues = sources
        .sort((a, b) => Number(a.dataset.optionPosition) - Number(b.dataset.optionPosition))
        .map((el) => el.value);

      const match = this.variants.find((variant) =>
        variant.options.every((optionValue, index) => optionValue === selectedValues[index])
      );

      if (!this.hiddenInput) return;
      if (!match) {
        this.hiddenInput.value = '';
        this.hiddenInput.disabled = true;
        if (this.submitBtn) this.submitBtn.disabled = true;
        return;
      }

      this.hiddenInput.disabled = false;
      this.hiddenInput.value = match.id;

      if (this.submitBtn) {
        this.submitBtn.disabled = !match.available;

        // Say so when the chosen combination is sold out, instead of a dimmed
        // "add" label. Labels come from the button's own data attributes
        // (locale strings rendered by upsell-product.liquid).
        const label = this.submitBtn.querySelector('.btn__text');
        const add = this.submitBtn.dataset.labelAdd;
        const soldOut = this.submitBtn.dataset.labelSoldOut;
        if (label && add && soldOut) label.textContent = match.available ? add : soldOut;
      }
    }
  }

  customElements.define('upsell-variant-select', UpsellVariantSelect);

  // [data-upsell-add-btn] is type="button" (see snippets/upsell-product.liquid
  // for why), so it needs its own explicit dispatch of the same theme:cart:add
  // event a type="submit" button would trigger via the theme's native
  // QuickAddProduct click handler - same downstream cart-add handling either
  // way. One delegated document listener, guarded so a Theme Editor section
  // reload re-inserting this script tag never binds it twice.
  if (!window.__upsellAddBtnBound) {
    window.__upsellAddBtnBound = true;

    document.addEventListener('click', function (event) {
      const button = event.target.closest('[data-upsell-add-btn]');
      if (!button || button.disabled) return;

      event.preventDefault();
      document.dispatchEvent(
        new CustomEvent('theme:cart:add', {
          detail: { button: button },
        })
      );
    });
  }
})();
